import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { authErrorMessage } from '../lib/authErrors';
import AuthLayout, { AuthError, authInputClass, authLinkClass, authPrimaryButtonClass } from '../components/AuthLayout';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, user } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();

  // Redirect if already logged in; ProtectedRoute sends each role to its home.
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { error } = await signIn(email.trim(), password);
      if (error) {
        console.error('Login error:', error);
        setError(authErrorMessage(error));
      }
      // Navigation happens through the effect above once the user state updates.
    } catch (err) {
      console.error('Authentication error:', err);
      setError(authErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Iniciar sesión"
      description={organization ? `Accede al portal de proveedores de ${organization.name}.` : 'Accede con el correo con el que fuiste invitado.'}
      footer="¿No tienes acceso? Pide una invitación al administrador de tu organización."
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Correo electrónico
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`mt-1 ${authInputClass}`}
            placeholder="correo@ejemplo.com"
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              Contraseña
            </label>
            <Link to="/forgot-password" className={`text-sm ${authLinkClass}`}>
              ¿Olvidaste tu contraseña?
            </Link>
          </div>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={`mt-1 ${authInputClass}`}
            placeholder="••••••••"
          />
        </div>

        <AuthError message={error} />

        <button type="submit" disabled={isLoading} className={authPrimaryButtonClass}>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {isLoading ? 'Ingresando…' : 'Iniciar sesión'}
        </button>
      </form>
    </AuthLayout>
  );
}
